import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { INK } from "../../../theme/tokens";
import { SystemReactTinyPopover, SystemReactTinyPopoverRootStyle } from "../../UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

type Props = {
	count: number;
	onViewAndDecide: () => void;
	onDismiss: () => void;
};

/**
 * The "N employees are waiting for decision" affordance: an orange "!" button in
 * the team toolbar that opens a card prompting the admin to review the open
 * decisions. "View and Decide" activates the pending-decision filter; "Dismissed"
 * hides the banner until a new employee needs a decision (see useStatusDecisionsBanner).
 */
export const StatusDecisionsBanner: FC<Props> = ({ count, onViewAndDecide, onDismiss }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const [isOpen, setIsOpen] = useState(false);

	const handleViewAndDecide = () => {
		track("team_status_decisions_banner_view", { count });
		setIsOpen(false);
		onViewAndDecide();
	};

	const handleDismiss = () => {
		track("team_status_decisions_banner_dismissed", { count });
		setIsOpen(false);
		onDismiss();
	};

	return (
		<SystemReactTinyPopover
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			positions={["bottom", "left", "top"]}
			align="end"
			contentRootStyle={{ ...SystemReactTinyPopoverRootStyle, padding: 0, maxWidth: 320 }}
			content={
				<Box data-testid="team-status-decisions-banner-card" sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
					<Typography sx={{ color: INK.PRIMARY, fontWeight: 700, fontSize: "16px" }}>{t("team.status.decisions.banner.title", { count })}</Typography>
					<Typography sx={{ color: INK.MUTED, fontSize: "14px", mb: 0.5 }}>{t("team.status.decisions.banner.subtitle")}</Typography>
					<Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
						<SystemButton data-testid="team-status-decisions-view-decide" buttonVariant="primary" onClick={handleViewAndDecide} sx={{ fontSize: "14px" }}>
							{t("team.status.decisions.banner.view_and_decide")}
						</SystemButton>
						<SystemButton data-testid="team-status-decisions-dismiss" buttonVariant="secondary" onClick={handleDismiss} sx={{ fontSize: "14px" }}>
							{t("team.status.decisions.banner.dismiss")}
						</SystemButton>
					</Box>
				</Box>
			}
		>
			<IconButton
				data-testid="team-status-decisions-banner-button"
				aria-label={t("team.status.decisions.banner.title", { count })}
				onClick={() => setIsOpen((prev) => !prev)}
				sx={(theme) => ({
					width: 40,
					height: 40,
					color: theme.palette.warning.contrastText,
					backgroundColor: theme.palette.warning.main,
					borderRadius: "8px",
					transition: "transform 140ms cubic-bezier(0.23, 1, 0.32, 1), background-color 140ms ease",
					"&:hover": { backgroundColor: theme.palette.warning.dark },
					"&:active": { transform: "scale(0.94)" },
					"@media (prefers-reduced-motion: reduce)": { transition: "background-color 140ms ease", "&:active": { transform: "none" } },
					"&:focus-visible": { outline: `2px solid ${alpha(theme.palette.warning.main, 0.5)}`, outlineOffset: 2 },
				})}
			>
				<PriorityHighIcon />
			</IconButton>
		</SystemReactTinyPopover>
	);
};
